import { useEffect, useId, useRef, type ReactNode } from "react";

/**
 * A dialog, in a tab (flow ticket 03).
 *
 * **Nothing here is new design.** `packages/ui/src/SettingsModal.tsx` is this
 * repository's one dialog, and its `.modal-backdrop` / `.modal` / `.modal-header`
 * / `.modal-body` rules are already in the web bundle, because `main.tsx` imports
 * `@cuekards/ui/styles.css`. So this is a web-local component wearing those class
 * names rather than a second stylesheet — and the behaviour is copied from that
 * component rather than reinvented, because the argument for each part of it
 * (`SettingsModal.tsx:20–45`) has not changed:
 *
 * - **A click on the backdrop does not close it.** There is no click handler on
 *   the backdrop at all; the absence is the feature. What is most likely to be
 *   open in one of these is a half-typed URL, and a stray click on the way past
 *   is the cheapest possible way to lose it.
 * - **Escape does**, from a listener on `window` in the bubble phase — not the
 *   capture phase, which belongs to a pending key rebinding, and not the
 *   dialog's own subtree, which would stop working the moment focus left it.
 *   The web's `useShortcuts("web", …)` is mounted on the prompter only and has
 *   no command on Escape, so there is nothing here to swallow it.
 * - **Focus goes in on open and back where it came from on close**, remembered
 *   rather than handed in, so the dialog knows nothing about what opened it.
 *
 * It is a *frame* — a title, a body and a row of buttons — rather than a shape
 * built around whatever is inside the first one. Flow ticket 06 mounts it for the
 * speech model's download offer, which shares none of the second dialog's
 * content and all of its behaviour.
 */
export function Modal({
  title,
  onClose,
  children,
  buttons,
}: {
  title: string;
  /** Escape, and the `✕`. A `Cancel` in {@link buttons} should call it too. */
  onClose: () => void;
  /** The dialog's body. */
  children: ReactNode;
  /** The row along the bottom, in reading order — the affirmative last. */
  buttons: ReactNode;
}): JSX.Element {
  const dialog = useRef<HTMLDivElement>(null);
  const heading = useId();

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  useEffect(() => {
    const returnTo = document.activeElement;
    dialog.current?.focus();
    return () => {
      if (returnTo instanceof HTMLElement) returnTo.focus();
    };
  }, []);

  return (
    // No `onClick`. That absence is the feature — see above.
    <div className="modal-backdrop">
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={heading}
        tabIndex={-1}
        ref={dialog}
      >
        <header className="modal-header">
          <h2 id={heading}>{title}</h2>
          <button
            type="button"
            className="modal-close"
            aria-label={`Close ${title}`}
            title={`Close ${title}`}
            onClick={onClose}
          >
            ✕
          </button>
        </header>

        <div className="modal-body">{children}</div>

        <footer className="web-modal-foot">{buttons}</footer>
      </div>
    </div>
  );
}
